import React, { useEffect, useMemo, useState } from "react";
import TiltCard from "../components/TiltCard";
import Carousel3D from "../components/Carousel3D";
import PageBanner, { SectionTitle } from "../components/PageBanner";
import { collegeNames, galleryImages, pageBanners, events } from "../data/mock";
import { useAllCollegeMedia } from "../hooks/useDriveMedia";
import { COLLEGE_SLUGS } from "../lib/driveMedia";
import { fetchSiteGallery, GALLERY_COLLEGE_ALBUMS, mergeGalleryItems } from "../lib/siteDrive";
import { AlertCircle, X } from "lucide-react";
// Write actions live on the signed-in dashboard. Public page is read-only.
// import { canEdit } from "../lib/access";

// See components/CollegeProfile.jsx — Google serves a Drive photograph from two addresses
// and occasionally refuses one of them.
function onImageError(e) {
  const img = e.currentTarget;
  const spare = img.dataset.fallback;
  if (!spare || img.src === spare) return;
  img.dataset.fallback = "";
  img.src = spare;
}

export default function Gallery() {
  const [active, setActive] = useState(null);
  const [tab, setTab] = useState("All");
  const media = useAllCollegeMedia();
  const [hq, setHq] = useState({ status: "loading", files: [], error: "", connected: false });

  useEffect(() => {
    let live = true;
    fetchSiteGallery().then((d) => {
      if (!live) return;
      setHq({
        status: d.connected ? "ready" : "fallback",
        files: d.files || [],
        error: d.error || "",
        connected: d.connected === true,
      });
    }).catch(() => {
      if (!live) return;
      setHq({ status: "fallback", files: [], error: "Drive is not connected", connected: false });
    });
    return () => { live = false; };
  }, []);

  // Photographs the colleges have uploaded to Drive, filed under the same college headings
  // as the built-in ones so the existing tabs keep working without any new categories.
  const driveItems = useMemo(() => {
    if (!media) return [];
    return COLLEGE_SLUGS.flatMap(slug =>
      (media[slug] ? media[slug].photos : []).map(p => ({
        id: p.id, url: p.url, fallback: p.fallback, title: p.title,
        category: collegeNames[slug], src: p.src, live: true,
      }))
    );
  }, [media]);

  const hqItems = (hq.files || []).map((p) => ({
    id: p.id, url: p.url, fallback: p.full || p.fallback, title: p.title, name: p.name,
    category: p.category, src: p.view || p.src, live: true,
  }));
  const eventItems = events.map(e => ({ url: e.url, title: e.title, category: "Milestones & Events", src: e.source }));
  const all = useMemo(
    () => mergeGalleryItems([...hqItems, ...driveItems, ...eventItems, ...galleryImages]),
    [hqItems, driveItems, eventItems],
  );
  const collegeCats = GALLERY_COLLEGE_ALBUMS.map((a) => a.category);
  const isCollegeTab = collegeCats.includes(tab);
  const filtered = tab === "All" ? all : all.filter(g => g.category === tab);

  return (
    <div className="page-enter">
      <PageBanner image={pageBanners.gallery} title="Gallery" crumb="Gallery" subtitle="Real campus photographs from our Government Medical Colleges across Andhra Pradesh." />
      <section style={{ padding: "50px 24px 10px" }}>
        <div style={{ maxWidth: 1300, margin: "0 auto" }}>
          <SectionTitle>Campus Showcase</SectionTitle>
          {hq.status === "loading" && (
            <div data-testid="gallery-loading" style={{ marginBottom: 14, fontSize: 13, color: "#78716C", fontWeight: 600 }}>Loading photographs from Drive…</div>
          )}
          {hq.status === "fallback" && (
            <div data-testid="gallery-drive-error" style={{ marginBottom: 14, padding: "12px 14px", background: "#FEF2F2", border: "1px solid rgba(220,38,38,0.28)", borderRadius: 12, color: "#B91C1C", fontSize: 13.5, display: "flex", gap: 8 }}>
              <AlertCircle size={16} style={{ flexShrink: 0, marginTop: 2 }} />
              {hq.error || "Drive is not connected"}. Campus photographs already on this page remain visible.
            </div>
          )}
          {hq.connected && hq.error && !hq.files.length && hq.status !== "loading" && (
            <div data-testid="gallery-drive-list-error" style={{ marginBottom: 14, padding: "12px 14px", background: "#FEF2F2", border: "1px solid rgba(220,38,38,0.28)", borderRadius: 12, color: "#B91C1C", fontSize: 13.5, display: "flex", gap: 8 }}>
              <AlertCircle size={16} style={{ flexShrink: 0, marginTop: 2 }} />
              {hq.error}
            </div>
          )}
          <div className="gallery-filters" data-testid="gallery-filters" style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 22, alignItems: "center" }}>
            <button data-testid="gallery-tab-all" onClick={() => setTab("All")} className={tab === "All" ? "btn-primary-3d" : "btn-outline-3d"} style={{ padding: "8px 16px", fontSize: 13 }}>
              All
            </button>
            <button data-testid="gallery-tab-milestones-events" onClick={() => setTab("Milestones & Events")} className={tab === "Milestones & Events" ? "btn-primary-3d" : "btn-outline-3d"} style={{ padding: "8px 16px", fontSize: 13 }}>
              Milestones & Events
            </button>
            <label className={isCollegeTab ? "gallery-college-wrap gallery-college-wrap-active" : "gallery-college-wrap"}>
              <span className="sr-only">Colleges</span>
              <select
                data-testid="gallery-college-dropdown"
                aria-label="Colleges"
                value={isCollegeTab ? tab : ""}
                onChange={(e) => setTab(e.target.value || "All")}
                className={isCollegeTab ? "gallery-college-select gallery-college-select-active" : "gallery-college-select"}
              >
                <option value="">Colleges</option>
                {GALLERY_COLLEGE_ALBUMS.map((a) => (
                  <option key={a.value} value={a.category}>{a.category}</option>
                ))}
              </select>
            </label>
          </div>
          {filtered.length > 0 && <Carousel3D key={tab} items={filtered} height={440} onSelect={setActive} />}
        </div>
      </section>

      <section style={{ padding: "30px 24px 60px" }}>
        <div style={{ maxWidth: 1300, margin: "0 auto", display: "grid", gap: 18 }} className="grid-eq grid-4">
          {filtered.length === 0 && hq.status !== "loading" && (
            <div data-testid="gallery-empty" style={{ gridColumn: "1/-1", textAlign: "center", padding: 40, color: "#78716C" }}>No photographs in this album yet.</div>
          )}
          {filtered.map((g, i) => (
            <TiltCard key={g.id || g.url} intensity={5}>
              <div data-testid={`gallery-item-${i}`} onClick={() => setActive(g)} style={{ cursor: "pointer", borderRadius: 20, overflow: "hidden", position: "relative", aspectRatio: "4/3" }}>
                <img src={g.url} alt={g.title} loading="lazy" onError={onImageError} data-fallback={g.fallback || ""} style={{ width: "100%", height: "100%", objectFit: "cover", transition: "transform 700ms" }} onMouseEnter={e => e.currentTarget.style.transform = "scale(1.08)"} onMouseLeave={e => e.currentTarget.style.transform = "scale(1)"} />
                <div style={{ position: "absolute", inset: 0, background: "linear-gradient(180deg, transparent 55%, rgba(28,25,23,0.85) 100%)" }} />
                <div style={{ position: "absolute", bottom: 14, left: 16, color: "#FFFFFF" }}>
                  <div style={{ fontSize: 10, letterSpacing: 1.5, color: "#FDE047", fontWeight: 700 }}>{g.category.toUpperCase()}</div>
                  <div className="font-serif" style={{ fontSize: 17, fontWeight: 700 }}>{g.title}</div>
                </div>
              </div>
            </TiltCard>
          ))}
        </div>
      </section>

      {active && (
        <div onClick={() => setActive(null)} data-testid="gallery-lightbox" style={{ position: "fixed", inset: 0, background: "rgba(67,20,7,0.88)", backdropFilter: "blur(6px)", zIndex: 200, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
          <div onClick={e => e.stopPropagation()} style={{ maxWidth: 1100, background: "#fff", borderRadius: 24, overflow: "hidden", position: "relative", boxShadow: "0 40px 80px rgba(0,0,0,0.5)", animation: "fadeUp 400ms ease-out" }}>
            <button data-testid="gallery-lightbox-close" onClick={() => setActive(null)} style={{ position: "absolute", right: 14, top: 14, background: "#DC2626", color: "#fff", border: "none", borderRadius: 12, padding: 10, cursor: "pointer", zIndex: 2 }}><X size={16} /></button>
            <img src={active.url} alt={active.title} onError={onImageError} data-fallback={active.fallback || ""} style={{ width: "100%", maxHeight: "75vh", objectFit: "cover", display: "block" }} />
            <div style={{ padding: 20 }}>
              <div style={{ fontSize: 11, letterSpacing: 1.5, color: "#EA580C", fontWeight: 700 }}>{active.category.toUpperCase()}</div>
              <div className="font-serif" style={{ fontSize: 22, color: "#1C1917", fontWeight: 700, marginTop: 4 }}>{active.title}</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
